import React, { useState } from 'react';
import { Play, CheckCircle2, ArrowRight, AlertTriangle } from 'lucide-react';
import { ErrorRule } from '../../../../types';
import { evaluateRuleSet, EvaluationResult, SampleResponse } from '../../../../utils/errorRuleset';
import { INPUT_CLASS, LABEL_CLASS } from './uiConstants';

interface TestPanelProps {
  rules: ErrorRule[];
}

export const TestPanel: React.FC<TestPanelProps> = ({ rules }) => {
  const [open, setOpen] = useState(false);
  const [sample, setSample] = useState<SampleResponse>({ statusCode: '', transient: false, exceptionType: '', body: '' });
  const [result, setResult] = useState<EvaluationResult | null>(null);

  const run = () => setResult(evaluateRuleSet(rules, sample));

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300">
        {open ? '▾' : '▸'} Test against a sample response
      </button>
      {open && (
        <div className="p-3 border-t border-slate-100 dark:border-slate-800 space-y-3">
          <p className="text-[11px] text-slate-400">
            Editor-side approximation of the executor's first-match evaluation. Set transient / status explicitly —
            the executor classifies those at runtime.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>Status code</label>
              <input value={sample.statusCode as any} onChange={(e) => setSample(s => ({ ...s, statusCode: e.target.value }))} placeholder="409" className={INPUT_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Exception type</label>
              <input value={sample.exceptionType} onChange={(e) => setSample(s => ({ ...s, exceptionType: e.target.value }))} placeholder="Aggregate.NotFound" className={INPUT_CLASS} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={!!sample.transient} onChange={(e) => setSample(s => ({ ...s, transient: e.target.checked }))} />
            Transient
          </label>
          <div>
            <label className={LABEL_CLASS}>Body</label>
            <textarea value={sample.body} onChange={(e) => setSample(s => ({ ...s, body: e.target.value }))} rows={4}
              placeholder='{"error":{"code":"content_filter"}}' className={`${INPUT_CLASS} font-mono`} />
          </div>
          <button type="button" onClick={run}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg">
            <Play size={14} /> Evaluate
          </button>

          {result && (
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 p-3 text-sm">
              {result.matchedIndex !== null ? (
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 size={16} /> Rule {result.matchedIndex + 1} matches <ArrowRight size={14} /> <span className="font-mono">{result.outcome}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <ArrowRight size={14} /> Falls through to <span className="font-mono">{result.outcome}</span>
                </div>
              )}
              {result.predicateError && (
                <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                  <AlertTriangle size={12} /> Body predicate could not evaluate: {result.predicateError}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
